import { useEffect, useCallback, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import Lottie from "react-lottie";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import { JiraBackground } from "@atlassian/product-wireframes";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    ufoEventRedirectToProduct,
    UfoCustomTimingKey,
    UfoCustomTimingWrapper,
    getFirstName,
    getIdentityBaseUrl
} from "@/components/signup/utils";
import { DwellTimeWrapper } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/components/dwell-time-wrapper";
import {
    useCloudStatus,
    useCloudStatusRedirection
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";
import { trackPerceivedProvisioningEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics";

import { useLoggedInUser } from "../../../../../../../../../utils";
import ProgressBar from "../../../sections/progress-bar";

import IconsAnimationLight from "./lotties/icons.light.json";
import EndProvisioningAnimation from "./lotties/provisioning-end-animation.json";
import ProvisioningAnimationLight from "./lotties/provisioning.light.json";
import { ModalOverlay } from "./modal-overlay";

const EndAnimationContainerStyle = css({
    width: 512
});

const EndAnimationParagraphStyle = css({
    margin: "16px",
    textAlign: "center",
    fontWeight: "400",
    lineHeight: "24px"
});

const modalContentStyle = css({
    display: "flex",
    flexFlow: "column",
    alignItems: "center",
    alignSelf: "center",
    position: "relative",
    zIndex: 1
});

const backgroundAnimationStyle = css({
    display: "none",
    [media.above.md]: {
        display: "block",
        position: "absolute",
        insetBlockStart: 0,
        insetInlineStart: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        borderRadius: "4px",
        pointerEvents: "none",
        overflow: "hidden"
    }
});

const iconAnimationStyle = css({
    maxWidth: "100%",
    width: "400px"
});

const EndAnimationHeaderStyle = css({
    textAlign: "center",
    fontSize: "24px",
    fontWeight: 653,
    lineHeight: "28px",
    marginBlockStart: 160,
    marginBlockEnd: 0
});

export const ControlAnimation = ({
    provisioningCompleteDefault = false
}: {
    provisioningCompleteDefault?: boolean;
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [provisioningComplete, setProvisioningComplete] = useState(
        provisioningCompleteDefault
    );
    const userInfo = useLoggedInUser();
    const name = getFirstName(userInfo.name);

    const {
        createCloudStatus,
        perceivedProvisioningStartTime,
        productActivationStatusResponses
    } = useCloudStatus();

    const {
        redirectUrl,
        deferRedirectView,
        setDeferRedirectView,
        resolveBlockingRedirectPromises
    } = useCloudStatusRedirection();

    const { products, signupId } = useSearchParameters();
    const productKey = products.length > 0 ? products[0] : undefined;

    const redirectCallback = useCallback(
        async (url: string) => {
            createAnalyticsEvent({
                eventType: "ui",
                action: "succeeded",
                actionSubject: "provisioning",
                actionSubjectId: "twcProvisioningScreen",
                source: "/ondemand/signup/confirmation"
            }).fire();

            createAnalyticsEvent({
                eventName: "redirectedToProduct",
                eventComponent: "confirmationPage",
                event: "redirected",
                eventType: "ui",
                action: "redirected"
            }).fire();

            trackPerceivedProvisioningEvent({
                createAnalyticsEvent,
                productKey,
                finalProvisioningScreen: "twc-provisioning-screen",
                startTimeSeconds: perceivedProvisioningStartTime,
                signupId
            });

            // SLO "good event" for redirect into product
            ufoEventRedirectToProduct.success({
                customData: {
                    reactComponent: "TwcProvisioningScreen",
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    finalProvisioningScreen: "twc-provisioning-screen"
                }
            });

            await resolveBlockingRedirectPromises();

            window.location.assign(url);
        },
        [
            createAnalyticsEvent,
            perceivedProvisioningStartTime,
            productKey,
            signupId,
            resolveBlockingRedirectPromises
        ]
    );

    useEffect(() => {
        if (
            createCloudStatus === "success" &&
            productActivationStatusResponses &&
            productActivationStatusResponses.length > 0
        ) {
            const twcOnboardingStatusResponse =
                productActivationStatusResponses[0];

            const twcOnboardingStatus =
                twcOnboardingStatusResponse?.twcOnboardingStatus;

            let onboardingUrl = redirectUrl;

            if (
                twcOnboardingStatus &&
                twcOnboardingStatus.status === "completed" &&
                twcOnboardingStatus.onboardingUrl
            ) {
                onboardingUrl = twcOnboardingStatus.onboardingUrl;
            } else if (
                twcOnboardingStatus &&
                twcOnboardingStatus.status === "failed"
            ) {
                // If Twc onboarding status failed, we should still redirect to the default product URL
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "failed",
                    actionSubject: "TwcOnboardingStatus Failed",
                    actionSubjectId: "twcProvisioningScreen",
                    source: "/ondemand/signup/confirmation",
                    attributes: {
                        reason: "TWC onboarding status response was failed and no onboarding URL was provided"
                    }
                }).fire();
            }

            setProvisioningComplete(true);

            setTimeout(() => {
                const url = `${getIdentityBaseUrl()}/login?${new URLSearchParams(
                    {
                        continue: onboardingUrl,
                        application: "wac--teamwork-collection"
                    }
                )}`;

                redirectCallback(url);
            }, 3500); // give time for final animation to play
        }
    }, [
        createCloudStatus,
        redirectCallback,
        createAnalyticsEvent,
        perceivedProvisioningStartTime,
        productKey,
        redirectUrl,
        productActivationStatusResponses
    ]);

    // Prevent use-cloud-status.tsx from immediately redirecting when provisioning is finished.
    useEffect(() => {
        if (!deferRedirectView) {
            if (setDeferRedirectView) {
                setDeferRedirectView("twc-provisioning-screen");
            }
        }
    });

    // Initial analytics events
    useEffect(() => {
        createAnalyticsEvent({
            eventType: "screen",
            name: "twcProvisioningScreen"
        }).fire();
    }, [createAnalyticsEvent]);

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={UfoCustomTimingKey.TWC_PROVISIONING_DWELL_TIME}
        >
            <DwellTimeWrapper
                eventData={{
                    action: "dwelled",
                    actionSubject: "screen",
                    actionSubjectId: "twcProvisioningScreen",
                    source: "/ondemand/signup/confirmation"
                }}
                initialPollInterval={5000}
                idleTimeout={120000}
                nextPollIntervalCallback={(x) => x + 5000}
            >
                <JiraBackground>
                    <ModalOverlay>
                        <div css={backgroundAnimationStyle}>
                            {!provisioningComplete ? (
                                <Lottie
                                    options={{
                                        loop: false,
                                        animationData:
                                            ProvisioningAnimationLight
                                    }}
                                />
                            ) : (
                                <Lottie
                                    options={{
                                        loop: false,
                                        animationData: EndProvisioningAnimation
                                    }}
                                />
                            )}
                        </div>
                        <div css={modalContentStyle}>
                            {!provisioningComplete ? (
                                <>
                                    <div css={iconAnimationStyle}>
                                        <Lottie
                                            options={{
                                                loop: true,
                                                animationData:
                                                    IconsAnimationLight
                                            }}
                                        />
                                    </div>
                                    <ProgressBar />
                                    <p
                                        css={EndAnimationParagraphStyle}
                                        data-testid="teamwork-collection-provisioning-message"
                                    >
                                        <FormattedMessage
                                            id="WacConfirmationPage.teamworkCollectionProvisioning"
                                            defaultMessage="Getting your collection ready—this will only take a moment. You will be redirected shortly."
                                        />
                                    </p>
                                </>
                            ) : (
                                <div css={EndAnimationContainerStyle}>
                                    <h3 css={EndAnimationHeaderStyle}>
                                        <FormattedMessage
                                            id="WacConfirmationPage.teamworkCollectionProvisioningComplete.name"
                                            defaultMessage="Hi {name},"
                                            values={{ name }}
                                        />
                                        <br />
                                        <FormattedMessage
                                            id="WacConfirmationPage.teamworkCollectionProvisioningComplete.welcome"
                                            defaultMessage="Welcome to Teamwork Collection"
                                        />
                                    </h3>
                                    <p
                                        css={EndAnimationParagraphStyle}
                                        data-testid="teamwork-collection-provisioning-message-complete"
                                    >
                                        <FormattedMessage
                                            id="WacConfirmationPage.teamworkCollectionProvisioningComplete"
                                            defaultMessage="You've unlocked a smarter way for your team to connect,{br}collaborate and get more done."
                                            values={{ br: <br /> }}
                                        />
                                    </p>
                                </div>
                            )}
                        </div>
                    </ModalOverlay>
                </JiraBackground>
            </DwellTimeWrapper>
        </UfoCustomTimingWrapper>
    );
};
